import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Menu, X, ChevronDown, User, LogOut, Search, Circle, EyeOff } from "lucide-react";
import { useAuth, API } from "@/App";
import { usePageVisibility } from "@/context/PageVisibilityContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [season, setSeason] = useState("");
  const searchInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isVisible } = usePageVisibility();

  // Poll live matches every 60s
  useEffect(() => {
    const fetchLive = async () => {
      try {
        const { data } = await axios.get(`${API}/live/matches`);
        setLiveCount(data.length);
      } catch (e) { /* silent */ }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 60000);
    return () => clearInterval(interval);
  }, []);

  // Current season label (from first competition)
  useEffect(() => {
    const fetchSeason = async () => {
      try {
        const { data } = await axios.get(`${API}/competitions`);
        const s = (data || []).map((c) => c.season).find(Boolean);
        if (s) setSeason(s);
      } catch (e) { /* silent */ }
    };
    fetchSeason();
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchOpen) return;
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(data);
      } catch (e) { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchOpen]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [searchOpen]);

  // Primary nav (per Master Prompt IA)
  const allNavLinks = [
    { href: "/", label: "Home" },
    { href: "/matches", label: "Matches", pageKey: "matches" },
    { href: "/fixtures", label: "Fixtures" },
    { href: "/results", label: "Results", pageKey: "results" },
    { href: "/tables", label: "Tables", pageKey: "tables" },
    { href: "/teams", label: "Clubs", pageKey: "teams" },
    { href: "/players", label: "Players", pageKey: "players" },
    { href: "/statistics", label: "Stats", pageKey: "statistics" },
    { href: "/competitions", label: "Competitions", pageKey: "competitions" },
    { href: "/national-teams", label: "National Teams", pageKey: "national_teams" },
    { href: "/news", label: "News", pageKey: "news" },
    { href: "/media", label: "Videos", pageKey: "media" },
    { href: "/tickets", label: "Tickets", pageKey: "tickets" },
    { href: "/shop", label: "Shop", pageKey: "shop" },
  ];

  const isAdmin = user?.role === "admin";
  // Admins see every link (with a hidden indicator). Visitors only see visible links.
  const navLinks = allNavLinks
    .map((l) => ({ ...l, hidden: l.pageKey ? !isVisible(l.pageKey) : false }))
    .filter((l) => isAdmin || !l.hidden);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const handleResultClick = (path) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults(null);
    navigate(path);
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-gray-200/20 bg-white/95 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60" data-testid="header">
        {/* Top strip: LIVE indicator + tagline */}
        <div className="bg-[#09090b] text-white text-xs">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {liveCount > 0 ? (
                <Link to="/matches" className="flex items-center gap-2 text-red-400 font-bold uppercase tracking-widest" data-testid="live-indicator">
                  <Circle className="w-2 h-2 fill-current animate-pulse" />
                  Live • {liveCount} match{liveCount === 1 ? '' : 'es'}
                </Link>
              ) : (
                <span className="text-gray-400 tracking-widest uppercase text-[10px]">The Home of Antigua and Barbuda Football</span>
              )}
            </div>
            <div className="hidden md:flex items-center gap-4 text-gray-400 text-[10px] uppercase tracking-widest">
              <span data-testid="header-season-label">{season ? `${season} Season` : ""}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20 gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo-link">
              <img 
                src={LOGO_URL} 
                alt="ABFA Football Centre" 
                className="h-14 w-auto"
              />
              <div className="hidden sm:block">
                <p className="font-heading font-bold text-lg uppercase tracking-tight text-[#09090b] leading-none">ABFA</p>
                <p className="text-[10px] text-[#009E60] uppercase tracking-widest font-bold">Football Centre</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-1 flex-1 justify-center" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`group relative px-3 py-2 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap flex items-center gap-1 ${isActive(link.href) ? "text-[#FFC72C]" : "text-gray-700 hover:text-[#09090b]"} ${link.hidden ? "opacity-50" : ""}`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={link.hidden ? "Hidden from visitors" : undefined}
                >
                  {link.hidden && <EyeOff className="w-3 h-3 text-orange-500" data-testid={`nav-hidden-${link.pageKey}`} />}
                  {link.label}
                  {/* Animated underline slide-in */}
                  <span
                    aria-hidden
                    className={`absolute left-3 right-3 bottom-0 h-0.5 bg-[#FFC72C] transform origin-left transition-transform duration-300 ${isActive(link.href) ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
                  />
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                data-testid="header-search-btn"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="hidden md:inline text-sm font-medium">{user.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to={user.role === "team_manager" ? "/manager" : "/admin"} className="flex items-center gap-2" data-testid="admin-dashboard-link">
                        <User className="w-4 h-4" />
                        {user.role === "team_manager" ? "Manager" : "Admin"} Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link 
                  to="/login"
                  className="hidden md:block btn-abfa-primary text-xs py-2 px-5"
                  data-testid="admin-login-btn"
                >
                  <span>Sign In</span>
                </Link>
              )}

              <button
                className="xl:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-6 py-3 text-sm font-bold uppercase tracking-widest flex items-center gap-2 ${
                    isActive(link.href) ? "text-[#FFC72C] bg-gray-50" : "text-gray-700"
                  } ${link.hidden ? "opacity-50" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.hidden && <EyeOff className="w-3 h-3 text-orange-500" />}
                  {link.label}
                </Link>
              ))}
              <div className="px-6 py-4 border-t border-gray-200">
                {user ? (
                  <>
                    <Link 
                      to={user.role === "team_manager" ? "/manager" : "/admin"} 
                      className="block w-full text-center btn-abfa-primary py-2 mb-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span>{user.role === "team_manager" ? "Manager" : "Admin"} Dashboard</span>
                    </Link>
                    <button 
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="w-full text-center btn-abfa-secondary py-2"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/login"
                    className="w-full btn-abfa-primary py-2 block text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>Sign In</span>
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Global Search Overlay */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-2xl p-0 gap-0" aria-describedby={undefined}>
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search players, clubs, matches, competitions..."
                className="w-full pl-12 pr-4 py-4 text-base border-0 focus:outline-none focus:ring-0"
                data-testid="global-search-input"
              />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-4" data-testid="global-search-results">
            {searchQuery.length < 2 ? (
              <p className="text-sm text-gray-400 text-center py-8">Type at least 2 characters to search</p>
            ) : !searchResults ? (
              <p className="text-sm text-gray-400 text-center py-8">Searching...</p>
            ) : searchResults.total === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No results for "{searchQuery}"</p>
            ) : (
              <div className="space-y-6">
                {searchResults.teams.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Clubs</h4>
                    <div className="space-y-1">
                      {searchResults.teams.map(t => (
                        <button
                          key={t.team_id}
                          onClick={() => handleResultClick(`/teams/${t.team_id}`)}
                          className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 text-left"
                          data-testid={`search-team-${t.team_id}`}
                        >
                          <img src={t.logo_url} alt="" className="w-8 h-8 object-contain" />
                          <div>
                            <p className="font-medium text-sm">{t.name}</p>
                            <p className="text-xs text-gray-500">{t.short_name}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.players.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Players</h4>
                    <div className="space-y-1">
                      {searchResults.players.map(p => (
                        <div key={p.player_id} className="flex items-center gap-3 p-2 hover:bg-gray-100">
                          <img src={p.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.competitions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Competitions</h4>
                    <div className="space-y-1">
                      {searchResults.competitions.map(c => (
                        <button
                          key={c.competition_id}
                          onClick={() => handleResultClick(`/competitions/${c.competition_id}`)}
                          className="w-full text-left p-2 hover:bg-gray-100 text-sm font-medium"
                          data-testid={`search-comp-${c.competition_id}`}
                        >
                          {c.name} <span className="text-xs text-gray-500">{c.season}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {searchResults.news.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">News</h4>
                    <div className="space-y-1">
                      {searchResults.news.map(n => (
                        <button
                          key={n.article_id}
                          onClick={() => handleResultClick(`/news/${n.slug || n.article_id}`)}
                          className="w-full text-left p-2 hover:bg-gray-100 text-sm"
                          data-testid={`search-news-${n.article_id}`}
                        >
                          <p className="font-medium truncate">{n.title}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
